import React, { useEffect, useRef } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import Home from "@pages/home/Home";
import styled from "@emotion/styled";
import Footer from "@components/footer/Footer";
import {
  AccountManagement,
  ChangePassword,
  MyPage,
  OneInquiry,
  SalesPolicy,
} from "@pages/myPage";
import { JoinPage, VerifyEmail } from "@pages/join";
import { ProductDetail } from "@pages/productDetail";

import FindPasswordPage from "@pages/findpassword/FindPasswordPage";
import {
  ProductPopup,
  RegisterProduct,
  SuccessRegister,
} from "@pages/register";
import ProfileEditPage from "@pages/profileEdit/ProfileEditPage";
import LoginPage from "@pages/Login/LoginPage";
import { Private, Public } from "@routes/index";
import useAuth from "@hook/useAuth";
import { useApiRequest } from "@utils/ApiRequest";
import { useQuery } from "@tanstack/react-query";
import useUserInfoStore from "@store/useUserInfoStore";
import { BankAccountManagement } from "@pages/myPage/_component";
import PleaseLogin from "@pages/home/PleaseLogin";

import AppRedirect from "@pages/app/AppRedirect";
import RegisterList from "@pages/registerList/RegisterList";
import CapturePopup from "@pages/capture/CapturePopup";
import CaptureDetail from "@pages/captureDetail/CaptureDetail";
import StockRegister from "@pages/stockRegister/StockRegister";
import ProductSearch from "@pages/search/ProductSearch";
import SuccessStockRegister from "@pages/stockRegister/SuccessStockRegister";
import CaptureList from "@pages/captureList/CaptureList";
import Faq from "@pages/myPage/_component/Faq";
import Capture from "@pages/capture/Capture";
import StockEdit from "@pages/stockEdit/StockEdit";
import ActivateUser from "@pages/join/ActiveUser";

const RouterContainer = styled.div({
  position: "relative",
  width: "100dvw",
  maxWidth: "560px",
  // height: "100dvh",
  minHeight: "100dvh",
  display: "flex",
  flexDirection: "column",
});

const RoutesWrapper = styled.div<{ haveFooter: boolean }>(({ haveFooter }) => ({
  flex: 1,
  overflow: "auto",
  paddingBottom: haveFooter ? "8rem" : "0",
  minWidth: "37.5rem",

  "&::-webkit-scrollbar": {
    display: "none",
  },
}));

const shouldRenderFooter = (pathName: string) => {
  const footerRoutes = ["/register-list", "/capture-list", "/my-page"];

  if (
    pathName === "/" ||
    footerRoutes.some((route) => pathName.startsWith(route))
  ) {
    return "default";
  }
  return null;
};

const Router = () => {
  const location = useLocation();
  const pathName = location.pathname;
  const routesWrapperRef = useRef<HTMLDivElement | null>(null);
  const { isAuthenticated } = useAuth();
  const { setUserInfo } = useUserInfoStore();
  const footerType = shouldRenderFooter(pathName);

  const { getUserInfo } = useApiRequest();

  const {
    data: userInfoData,
    isSuccess: userInfoSuccess,
  } = useQuery({
    queryKey: ["userInfo"],
    queryFn: getUserInfo,
    enabled: isAuthenticated,
  });

  useEffect(() => {
    setUserInfo(userInfoData);
  }, [userInfoData, userInfoSuccess, setUserInfo]);

  useEffect(() => {
    if (routesWrapperRef.current) {
      routesWrapperRef.current.scrollTo(0, 0);
    }
  }, [location.pathname]);

  return (
    <RouterContainer>
      <RoutesWrapper
        haveFooter={footerType === "default"}
        ref={routesWrapperRef}>
        <Routes>
          {
            <>
              <Route element={<Public />}>
                <Route
                  path="/"
                  element={
                    isAuthenticated ? (
                      <Home />
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                {/* 이메일 인증 페이지 */}
                <Route path="/verify-email" element={<VerifyEmail />} />

                {/* 회원가입 페이지 */}
                <Route path="/join" element={<JoinPage />} />

                {/* 회원가입 활성화 */}
                <Route path="/activate-user" element={<ActivateUser />} />

                {/* 로그인 페이지 */}
                <Route path="/login" element={<LoginPage />} />

                {/* 로그인 페이지 */}
                <Route path="/please-login" element={<PleaseLogin />} />

                {/* 비밀번호찾기 페이지 */}
                <Route path="/find-password" element={<FindPasswordPage />} />

                {/* 앱 실행 REDIRECT URL */}
                <Route path="/app/redirect/:path" element={<AppRedirect />} />

                {/*상품 상세정보*/}
                <Route
                  path="/product-detail/:id/:status"
                  element={<ProductDetail />}
                />
              </Route>

              <Route element={<Private />}>
              {/* 상품 등록 (capture) */}
              <Route path="/capture" element={<Capture />} />
              <Route path="/capture-list" element={<CaptureList />} />
              <Route path="/capture-popup" element={<CapturePopup />} />
              <Route path="/capture-detail" element={<CaptureDetail />} />

              {/* 검색 */}
              <Route path="search" element={<ProductSearch />} />

              {/*등록 페이지*/}
              <Route path="/register/:id" element={<RegisterProduct />} />
              <Route path="/stock-register/:id" element={<StockRegister />} />
              <Route
                path="/register/:id/:status"
                element={<RegisterProduct />}
              />
              <Route
                path="/stock-register/:id/:status"
                element={<StockRegister />}
              />
              <Route path="/product-popup" element={<ProductPopup />} />

              {/*등록 성공 페이지*/}
              <Route
                path="/register-success/:id/:status"
                element={<SuccessRegister />}
              />
              <Route
                path="/stock-success/:id/:status"
                element={<SuccessStockRegister />}
              />

              {/* 등록 내역 리스트 */}
              <Route path="register-list" element={<RegisterList />} />

              {/* 재고상품 수정 페이지 */}
              <Route path="/stock-edit/:id/:status" element={<StockEdit />} />

              {/*마이페이지*/}
              <Route path="/my-page" element={<MyPage />} />

              {/*프로필 편집 페이지*/}
              <Route path="/profile-edit" element={<ProfileEditPage />} />

              {/*비밀번호 변경*/}
              <Route path="/change-password" element={<ChangePassword />} />

              {/* FAQ */}
              <Route path="/faq" element={<Faq />} />

              {/* 1:1 문의하기 */}
              <Route path="/one-inquiry" element={<OneInquiry />} />

              {/*판매 정책 설정*/}
              <Route path="/sales-policy" element={<SalesPolicy />} />

              {/*계좌관리*/}
              <Route
                path="/bank-account-management"
                element={<BankAccountManagement />}
              />
              {/* 계정관리 */}
              <Route
                path="/account-management"
                element={<AccountManagement />}
              />
              </Route>
            </>
          }
        </Routes>
      </RoutesWrapper>

      {footerType === "default" && <Footer />}
    </RouterContainer>
  );
};

export default Router;
